import qs from 'query-string'

import {
  getURLFromAPIType,
  getIsPublicDocFromAPIType,
} from '../../function/getEditorApiEndpoint'
import axios from '../../function/axios/editorInstance'
import axiosMarketplace from '../../function/axiosInstance'
import extract from '../../function/extract'
import { useEditorStore } from '../../store/useEditorStore'

// y-sweet calls this on mount — before EditorRootWithToken's effect has seeded
// useEditorStore — so the editor-token interceptor would otherwise skip the
// header and the call would 403. wait for the store to populate, then let the
// interceptor attach x-editor-token like any other editor call.
async function waitForEditorStore(): Promise<void> {
  if (useEditorStore.getState().editorData) return
  await new Promise<void>(resolve => {
    const unsubscribe = useEditorStore.subscribe(state => {
      if (state.editorData) {
        unsubscribe()
        resolve()
      }
    })
  })
}

export async function getClientToken(docId: string, itemType: PepperDocTypes) {
  await waitForEditorStore()
  const response = await axios(
    `/v2/documents/${docId}/token?is_public=${getIsPublicDocFromAPIType(
      itemType
    )}`
  )
  return response.data
}

export async function getEditorToken({
  documentId,
  itemId,
  itemType,
}: {
  documentId: string
  itemId: string
  itemType: PepperDocTypes
}) {
  let url: string
  const v1EditorTypes: PepperDocTypes[] = ['BRIEF', 'NOTE']
  if (v1EditorTypes.includes(itemType)) {
    url = `/v1/editor/${documentId}`
  } else {
    url = `/${getURLFromAPIType(
      itemType
    )}/${itemId}/editor?exclude_content=true`
  }
  const response = await axios.get<{ 'x-editor-token': string }>(url, {
    params: { skipTokenCheck: true },
  })
  return response.data['x-editor-token']
}

export const getDocsContent = async ({
  id,
  apiType,
  excludeContent,
}: {
  id: string
  apiType?: string
  excludeContent?: boolean
}): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(apiType)}/${id}/editor`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
          exclude_content: excludeContent,
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(endpoint)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getTemplateContent = async ({
  templateId,
  assignmentId,
  apiType,
}: {
  templateId: string
  assignmentId: string
  apiType?: string
}): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/templates/${templateId}/editor/content`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
          assignment_id: assignmentId,
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(endpoint)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getDocsVersions = async ({
  id,
  page,
  limit,
  apiType,
}: {
  id: string
  page?: number
  limit?: number
  apiType?: string
}): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(apiType)}/${id}/editor/versions`,
        query: {
          limit: limit ?? 100,
          page: page ?? 1,
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(endpoint)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getDocsVersionsYjs = async ({
  id,
  apiType,
  startIndex,
  states,
  parentVersion,
  pageSize = 10,
}: {
  id: string
  apiType?: string
  parentVersion?: string | number | null
  startIndex: number
  states?: VersionStates[]
  pageSize?: number
}): Promise<GetDocsVersionYJSResponse> => {
  try {
    const query = {
      start: startIndex,
      end: startIndex + pageSize,
      refetch: startIndex === 0,
      parentVersion,
      is_public: getIsPublicDocFromAPIType(apiType),
      ...(states && { states: states?.join(',') }),
    }
    const urlType = getURLFromAPIType(apiType)
    const endpoint = qs.stringifyUrl(
      {
        url: `/${urlType}/${id}/editor/versions`,
        query,
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(endpoint)
    response.data.page = query
    // v1 yjs returns versions newest-first, v2 returns them oldest-first;
    // consumers (EditorRoot, getLatestVersionId) assume versions[0] is the
    // newest. detect order via createdAt of the first vs last item and
    // reverse if needed so both backends look the same to callers.
    const versions: EditorVersionList[] | undefined = response.data?.versions
    if (versions && versions.length > 1) {
      const firstAt = new Date(versions[0].createdAt).getTime()
      const lastAt = new Date(versions[versions.length - 1].createdAt).getTime()
      if (firstAt < lastAt) {
        response.data.versions = [...versions].reverse()
      }
    }
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getDocChildVersions = async ({
  assignmentId,
  apiType,
  parentVersion,
  startIndex,
  latest,
  states,
}: {
  assignmentId: string
  apiType?: string
  parentVersion: number
  startIndex: number
  latest: boolean
  states?: VersionStates[]
}): Promise<ApiResponse> => {
  try {
    const query = {
      childStart: startIndex,
      childEnd: startIndex + 10,
      parentVersion,
      latest,
      is_public: getIsPublicDocFromAPIType(apiType),
      ...(states && { states: states?.join(',') }),
    }
    const urlType = getURLFromAPIType(apiType)
    const endpoint = qs.stringifyUrl(
      {
        url: `/${urlType}/${assignmentId}/editor/versions`,
        query,
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(endpoint)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const postDocsContent = async ({
  id,
  body,
  apiType,
}: EditorServerData): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(apiType)}/${id}/editor`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.put(endpoint, body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updatePrivateItemTitle = async ({
  id,
  body,
  apiType,
}: EditorServerData): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `${getURLFromAPIType(apiType)}/${id}`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axiosMarketplace.put(endpoint, body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updateDocsExtras = async ({
  id,
  body,
  apiType,
}: {
  id: string
  body: {
    extras: Record<string, unknown>
  }
  apiType?: string
}): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(apiType)}/${id}/editor`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.put(endpoint, body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updateDocMetaData = async ({
  id,
  description,
}: // keywords
{
  id: string
  description?: string
  // keywords?: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/assignments/${id}/editor`, {
      meta_data: {
        meta_description: description,
        // keywords
      },
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updateDocsVersions = async ({
  id,
  versionId,
  apiType,
}: {
  id: string
  versionId: string
  apiType?: string
}): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(
          apiType
        )}/${id}/editor/versions/${versionId}/restore`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.put(endpoint)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getDocAsPerVersion = async ({
  id,
  versionId,
  apiType,
}: {
  id: string
  versionId: string
  apiType?: string
}): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(
          apiType
        )}/${id}/editor/versions/${versionId}`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(endpoint)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const publicAccessAssignment = async ({
  id,
  publicRead,
  publicWrite,
  publicComment,
  apiType,
}: {
  id: string
  publicRead: boolean
  publicWrite: boolean
  publicComment: boolean
  apiType?: string
}): Promise<ApiResponse> => {
  try {
    const payload = {
      public_read: publicRead,
      public_write: publicWrite,
      public_comment: publicComment,
    }
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(apiType)}/${id}/editor/permission`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.put(endpoint, payload)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getTemplateInformation = async ({
  id,
}: {
  id: string
}): Promise<TemplateDetailsResponse> => {
  try {
    const response = await axiosMarketplace.get(`/templates/${id}`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const deleteTemplate = async ({
  id,
}: {
  id: string
}): Promise<TemplateDetailsResponse> => {
  try {
    const response = await axiosMarketplace.delete(`/templates/${id}`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getReferences = async ({
  id,
  claim,
  apiType,
}: {
  id: string
  claim: string
  apiType?: string
}): Promise<ApiResponse> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/${getURLFromAPIType(apiType)}/${id}/reference_text`,
        query: {
          is_public: getIsPublicDocFromAPIType(apiType),
        },
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.post(endpoint, {
      claim,
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}
